import { percentage } from "../helpers";
import moment from "moment";
import { Moment } from "moment-timezone";

export type DataPoint = { id: string; x: number; y: number };
export type ChartInputData = {
  id: string;
  timestamp: number;
  [key: string]: string | number;
};
export type ChartConfig = {
  label: string;
  description?: string;
  module_id: number;
  axis: {
    x: { label: string };
    y: { max: number | null };
    y2?: { max: number | null; label: string };
  };
  group: "ambient_climates" | "agronomics" | "mildew_risk" | "soil_climates";
  legend: boolean;
  metrics: {
    [key: string]: {
      type: "line" | "area" | "bar" | "scatter";
      hidden: boolean;
      property?: string;
      axisId?: string;
      includeValues?: boolean;
      persist?: boolean;
      style?: "dashed" | "solid";
      formatter?: (
        value: Record<string, string | number>,
        field: { ec_division_factor: string }
      ) => number;
      aggregation?: (data: ChartInputData[]) => DataPoint[];
    };
  };
  table: string[];
};

const toNumber = (value: string | number) => {
  return typeof value === "string" ? parseFloat(value) : value;
};

export default {
  ambient_climate: {
    label: "Ambient Climate",
    module_id: 3,
    axis: {
      x: { label: "" },
      y: { max: null },
    },
    group: "ambient_climates",
    legend: true,
    metrics: {
      ambient_temp: { type: "line", hidden: false, property: "temperature" },
      humidity: {
        type: "line",
        hidden: false,
        formatter: (value) => percentage(value.humidity, 1, 0),
      },
    },
    table: ["ambient_temp", "humidity"],
  },
  agronomics_overview: {
    label: "Agronomics Overview",
    module_id: 2,
    axis: {
      x: { label: "" },
      y: { max: null },
    },
    group: "agronomics",
    legend: true,
    metrics: {
      ag_ph_in: { type: "line", hidden: false, property: "ph_in" },
      ag_ph_out: { type: "line", hidden: false, property: "ph_out" },
      ag_ph_bag: { type: "line", hidden: false, property: "ph_bag" },
      ag_ec_in: { type: "line", hidden: false, property: "ec_in" },
      ag_ec_out: { type: "line", hidden: false, property: "ec_out" },
      ag_ec_sum: { type: "line", hidden: false, property: "ec_sum" },
      ag_ec_bag: { type: "line", hidden: false, property: "ec_bag" },
      ag_run_off: { type: "area", hidden: false, property: "run_off" },
      ag_moisture: { type: "line", hidden: false, property: "moisture" },
      ag_drain_target: {
        type: "line",
        hidden: false,
        property: "drain_target",
        persist: true,
        style: "dashed",
      },
    },
    table: [
      "ag_ph_in",
      "ag_ph_out",
      "ag_ph_bag",
      "ag_ec_in",
      "ag_ec_out",
      "ag_ec_sum",
      "ag_ec_bag",
      "ag_ml_in",
      "ag_ml_out",
      "ag_run_off",
      "ag_moisture",
      "ag_water_cubes",
      "ag_irrigations_per_day",
      "ag_total_run_time",
      "ag_litres_per_day",
      "ag_drain_target",
      "ag_ph_target",
      "ag_ec_target",
    ],
  },
  agronomics_ph: {
    label: "Agronomics - pH",
    module_id: 2,
    axis: {
      x: { label: "pH" },
      y: { max: 9 },
    },
    group: "agronomics",
    legend: true,
    metrics: {
      ag_ph_in: { type: "area", hidden: false, property: "ph_in" },
      ag_ph_out: { type: "area", hidden: false, property: "ph_out" },
      ag_ph_bag: { type: "line", hidden: false, property: "ph_bag" },
      ag_ph_target: {
        type: "line",
        hidden: false,
        property: "ph_target",
        persist: true,
        style: "dashed",
      },
    },
    table: ["ag_ph_in", "ag_ph_out", "ag_ph_bag", "ag_ph_target"],
  },
  agronomics_ec: {
    label: "Agronomics - EC",
    module_id: 2,
    axis: {
      x: { label: "EC" },
      y: { max: 5 },
    },
    group: "agronomics",
    legend: true,
    metrics: {
      ag_ec_in: { type: "area", hidden: false, property: "ec_in" },
      ag_ec_out: { type: "area", hidden: false, property: "ec_out" },
      ag_ec_sum: { type: "line", hidden: false, property: "ec_sum" },
      ag_ec_bag: { type: "line", hidden: false, property: "ec_bag" },
      ag_ec_target: {
        type: "line",
        hidden: false,
        property: "ec_target",
        persist: true,
        style: "dashed",
      },
    },
    table: ["ag_ec_in", "ag_ec_out", "ag_ec_sum", "ag_ec_bag", "ag_ec_target"],
  },
  vpd: {
    label: "VPD, Solar Irradiance and Wind Speed",
    module_id: 3,
    axis: {
      x: { label: "hPa" },
      y: { max: null },
    },
    group: "ambient_climates",
    legend: false,
    metrics: {
      vpd: { type: "line", hidden: false },
      light: { type: "line", hidden: false },
      wind_speed: { type: "line", hidden: false },
    },
    table: ["vpd", "light", "wind_speed", "wind_direction"],
  },
  delta_t: {
    label: "Delta T Spray Advisory",
    description:
      "Delta T is calculated from dry bulb temperature and relative humidity. " +
      "Spray conditions are typically preferred between 2°C and 8°C, marginal between 8°C and 10°C, and unsuitable above 10°C.",
    module_id: 3,
    axis: {
      x: { label: "" },
      y: { max: 15 },
    },
    group: "ambient_climates",
    legend: true,
    metrics: {
      delta_t: { type: "line", hidden: false },
    },
    table: ["delta_t", "ambient_temp", "humidity"],
  },
  mildew: {
    description:
      // eslint-disable-next-line no-useless-concat
      "This is a decision support system designed to support the grower to have confidence to spray only when conditions are favourable for disease development. " +
      "The Decision Support System (DSS) is based on a rule-based prediction system which measures the accumulated number of hours of disease conducive conditions where the temperatures are >15.5 C and <30C with a relative humidity of >60%. " +
      "The Y-axis of the graph indicates the number of accumulated hours where both parameters are met, with the X-axis showing the date. " +
      "When the graph reaches amber (115 hours) the grower should prepare to spray, such that they spray as the graph reaches red (125 hours). " +
      "Once a fungicide has been applied, the system should be reset. When the line on the graph rises steeply there is a high risk indicating more frequent fungicide applications as the accumulated hours increase quickly, if the line on the graph is flatter there is a lower risk. " +
      "The grower should be guided in the modes of action they use by the factsheet 29/16: 'Control of strawberry powdery mildew under protection'.",
    label: "Mildew Risk",
    module_id: 1,
    axis: {
      x: { label: "" },
      y: { max: null },
    },
    group: "mildew_risk",
    legend: false,
    metrics: {
      mildew: { type: "line", hidden: false, property: "value" },
    },
    table: ["mildew"],
  },
  growing_hours: {
    label: "GDH and Accumulated Solar Radiation",
    module_id: 3,
    axis: {
      x: { label: "Hours" },
      y: { max: null },
      y2: { max: null, label: "MJ/m²" },
    },
    group: "ambient_climates",
    legend: true,
    metrics: {
      growing_hours: {
        type: "area",
        hidden: false,
        aggregation: (data) => {
          const min = 4.5;
          const max = 32;
          const points: DataPoint[] = [];
          let hours = 0;
          let lastTimestamp: Moment | null = null;
          for (const val of data) {
            const temp = toNumber(val.temperature);
            const timestamp = moment(val.timestamp);
            const delta = Math.abs(
              lastTimestamp?.diff(timestamp, "hours", true) ?? 0.25 // default to 15 minutes
            );
            lastTimestamp = timestamp;
            if (temp > min && temp < max) {
              hours = parseFloat(((temp - min) * delta + hours).toFixed(2));
            }
            points.push({ id: val.id, x: val.timestamp, y: hours });
          }

          return points;
        },
      },
      accumulated_light: {
        type: "area",
        hidden: false,
        axisId: "y2",
        aggregation: (data) => {
          const points: DataPoint[] = [];
          let light = 0;
          let lastTimestamp: Moment | null = null;
          for (const val of data) {
            const timestamp = moment(val.timestamp);
            const delta = Math.abs(
              lastTimestamp?.diff(timestamp, "seconds", true) ?? 15 * 60 // default to 15 minutes
            );
            lastTimestamp = timestamp;
            const l = toNumber(val.light);
            if (l > 0) {
              light = parseFloat(((l * delta) / 1e6 + light).toFixed(2));
            }
            points.push({ id: val.id, x: val.timestamp, y: light });
          }

          return points;
        },
      },
    },
    table: ["growing_hours"],
  },
  chilling_hours: {
    label: "Chilling Hours",
    module_id: 3,
    axis: {
      x: { label: "Hours" },
      y: { max: null },
    },
    group: "ambient_climates",
    legend: false,
    metrics: {
      chilling_hours: {
        type: "area",
        hidden: false,
        aggregation: (data) => {
          const hours: DataPoint[] = [];

          data
            .filter((a) => {
              const minutes = moment(a.timestamp).minutes();
              return minutes >= 0 && minutes < 15;
            })
            .reduce((acc, val) => {
              const temp = toNumber(val.temperature);
              const calc =
                temp < 7
                  ? parseFloat((acc + 1).toFixed(2))
                  : parseFloat(acc.toFixed(2));
              hours.push({ id: val.id, x: val.timestamp, y: calc });
              return calc;
            }, 0);
          return hours;
        },
      },
    },
    table: ["chilling_hours"],
  },
  soil_climate: {
    label: "Root Zone Moisture Content",
    module_id: 4,
    axis: {
      x: { label: "" },
      y: { max: null },
    },
    group: "soil_climates",
    legend: true,
    metrics: {
      moisture_content: { type: "line", hidden: false },
      squeeze: { type: "scatter", hidden: false },
    },
    table: ["moisture_content"],
  },
  soil_moisture: {
    label: "Moisture Content",
    module_id: 4,
    axis: {
      x: { label: "" },
      y: { max: null },
    },
    group: "soil_climates",
    legend: true,
    metrics: {
      moisture_10: { type: "line", hidden: false },
      moisture_20: { type: "line", hidden: false },
      moisture_30: { type: "line", hidden: false },
      moisture_40: { type: "line", hidden: false },
      moisture_50: { type: "line", hidden: false },
      moisture_60: { type: "line", hidden: false },
      moisture_70: { type: "line", hidden: false },
      moisture_80: { type: "line", hidden: false },
      moisture_90: { type: "line", hidden: false },
    },
    table: [
      "moisture_10",
      "moisture_20",
      "moisture_30",
      "moisture_40",
      "moisture_50",
      "moisture_60",
      "moisture_70",
      "moisture_80",
      "moisture_90",
    ],
  },
  soil_moisture_gains: {
    label: "Crop Water Usage & Gains",
    module_id: 4,
    axis: {
      x: { label: "mm" },
      y: { max: null },
    },
    group: "soil_climates",
    legend: true,
    metrics: {
      //    gains: { type: "bar", hidden: false }, // must remain for chart rendering
      gains_pos: { type: "bar", hidden: false },
      gains_neg: { type: "bar", hidden: false },
      // gains_daily: { type: "line", hidden: false },
    },
    table: ["gains"],
  },
  vic: {
    label: "Conductivity",
    module_id: 4,
    axis: {
      x: { label: "" },
      y: { max: null },
    },
    group: "soil_climates",
    legend: true,
    metrics: {
      vic_10: { type: "line", hidden: false },
      vic_20: { type: "line", hidden: false },
      vic_30: { type: "line", hidden: false },
      vic_40: { type: "line", hidden: false },
      vic_50: { type: "line", hidden: false },
      vic_60: { type: "line", hidden: false },
      vic_70: { type: "line", hidden: false },
      vic_80: { type: "line", hidden: false },
      vic_90: { type: "line", hidden: false },
    },
    table: [
      "vic_10",
      "vic_20",
      "vic_30",
      "vic_40",
      "vic_50",
      "vic_60",
      "vic_70",
      "vic_80",
      "vic_90",
    ],
  },
  soil_temp: {
    label: "Temperature",
    module_id: 4,
    axis: {
      x: { label: "" },
      y: { max: null },
    },
    group: "soil_climates",
    legend: true,
    metrics: {
      soil_temp_10: { type: "line", hidden: false },
      soil_temp_20: { type: "line", hidden: false },
      soil_temp_30: { type: "line", hidden: false },
      soil_temp_40: { type: "line", hidden: false },
      soil_temp_50: { type: "line", hidden: false },
      soil_temp_60: { type: "line", hidden: false },
      soil_temp_70: { type: "line", hidden: false },
      soil_temp_80: { type: "line", hidden: false },
      soil_temp_90: { type: "line", hidden: false },
    },
    table: [
      "soil_temp_10",
      "soil_temp_20",
      "soil_temp_30",
      "soil_temp_40",
      "soil_temp_50",
      "soil_temp_60",
      "soil_temp_70",
      "soil_temp_80",
      "soil_temp_90",
    ],
  },
  rainfall: {
    label: "Rainfall",
    module_id: 4,
    axis: {
      x: { label: "" },
      y: { max: null },
      y2: { max: null, label: "Water (cubes)" },
    },
    group: "soil_climates",
    legend: true,
    metrics: {
      rainfall: { type: "bar", hidden: false },
      water_cubes: { type: "bar", hidden: false },
    },
    table: ["rainfall"],
  },
  run_off: {
    label: "Run-Off",
    module_id: 4,
    axis: {
      x: { label: "" },
      y: { max: null },
    },
    group: "soil_climates",
    legend: true,
    metrics: {
      run_off_in: { type: "bar", hidden: false },
      run_off_out: { type: "bar", hidden: false },
      run_off: { type: "area", hidden: false, includeValues: true },
    },
    table: ["run_off_in", "run_off_out", "run_off"],
  },
  accumulated_run_off: {
    label: "Accumulated Run-Off",
    module_id: 4,
    axis: {
      x: { label: "" },
      y: { max: null },
    },
    group: "soil_climates",
    legend: true,
    metrics: {
      // note: processing is done in ChartAccumulatedRunOff.tsx
      run_off: { type: "area", hidden: false, includeValues: true },
      run_off_in: { type: "area", hidden: false },
      run_off_out: { type: "area", hidden: false },
    },
    table: ["run_off_in", "run_off_out", "run_off"],
  },
  ph: {
    label: "pH",
    module_id: 4,
    axis: {
      x: { label: "pH" },
      y: { max: null },
    },
    group: "soil_climates",
    legend: true,
    metrics: {
      ph_in: { type: "area", hidden: false },
      ph_out: { type: "area", hidden: false },
      ph_bag: { type: "line", hidden: false },
    },
    table: ["ph_in", "ph_out", "ph_bag"],
  },
  ec: {
    label: "EC",
    module_id: 4,
    axis: {
      x: { label: "EC" },
      y: { max: null },
    },
    group: "soil_climates",
    legend: true,
    metrics: {
      ec_in: {
        type: "area",
        hidden: false,
        formatter: (value, field) => {
          var factor = field?.ec_division_factor
            ? parseFloat(field.ec_division_factor)
            : 10000;
          return toNumber(value.ec_in) / factor;
        },
      },
      ec_out: {
        type: "area",
        hidden: false,
        formatter: (value, field) => {
          var factor = field?.ec_division_factor
            ? parseFloat(field.ec_division_factor)
            : 10000;
          return toNumber(value.ec_out) / factor;
        },
      },
      ec_sum: {
        type: "line",
        hidden: false,
        formatter: (value, field) => {
          var factor = field?.ec_division_factor
            ? parseFloat(field.ec_division_factor)
            : 10000;
          return toNumber(value.ec_sum) / factor;
        },
      },
      ec_bag: {
        type: "line",
        hidden: false,
        formatter: (value, field) => {
          var factor = field?.ec_division_factor
            ? parseFloat(field.ec_division_factor)
            : 10000;
          return toNumber(value.ec_bag) / factor;
        },
      },
    },
    table: ["ec_in", "ec_out", "ec_sum", "ec_bag"],
  },
} satisfies Record<string, ChartConfig>;

/**
 * Field-aware soil-climate chart factory.
 *
 * For a field with rootzone profiles, this returns the legacy chart plus one
 * chart config per non-archived profile (e.g. "Root Zone — Top 30cm",
 * "Root Zone — Top 60cm"). Profile chart ids are prefixed
 * `soil_climate__<profile_id>` so callers (chart routing, parameter filtering)
 * can derive the profile id from the chart id.
 */
export type RootzoneProfile = {
  id: string;
  name: string;
  depths: number[] | null;
  active: boolean;
  archived: boolean;
};

export type SoilClimateChartConfig = ChartConfig & {
  id: string;
  profileId: string;
  isActiveProfile: boolean;
};

export const buildSoilClimateCharts = (
  field: { rootzone_profiles?: RootzoneProfile[] | null } | null | undefined
): SoilClimateChartConfig[] => {
  const baseChart: SoilClimateChartConfig = {
    id: "soil_climate",
    profileId: "default",
    isActiveProfile: true,
    label: "Root Zone Moisture Content",
    module_id: 4,
    axis: { x: { label: "" }, y: { max: null } },
    group: "soil_climates",
    legend: true,
    metrics: {
      moisture_content: { type: "line", hidden: false },
      squeeze: { type: "scatter", hidden: false },
    },
    table: ["moisture_content"],
  };
  const profiles = (field?.rootzone_profiles ?? []).filter(
    (p) => !p.archived
  );
  if (profiles.length === 0) {
    return [baseChart];
  }
  const profileCharts: SoilClimateChartConfig[] = profiles.map(
    (p): SoilClimateChartConfig => ({
      id: `soil_climate__${p.id}`,
      profileId: p.id,
      isActiveProfile: !!p.active,
      label: `Root Zone — ${p.name}`,
      module_id: 4,
      axis: { x: { label: "" }, y: { max: null } },
      group: "soil_climates",
      legend: true,
      metrics: {
        moisture_content: { type: "line", hidden: false },
        squeeze: { type: "scatter", hidden: false },
      },
      table: ["moisture_content"],
    })
  );
  return [baseChart, ...profileCharts];
};

/**
 * Parses a chart id back into a profile id, or null if the chart is not a
 * soil-climate per-profile chart. Used by chart routing + parameter filtering
 * to figure out which profile's series + parameters to draw.
 */
export const parseSoilClimateProfileId = (chartId: string): string | null => {
  if (chartId === "soil_climate") return "default";
  if (chartId.startsWith("soil_climate__")) {
    return chartId.slice("soil_climate__".length);
  }
  return null;
};
